import { cloneDeep } from "lodash"

import { ACTIVITY_SESSION_TYPE, SECTION_TYPE } from "@/app/report/common/constants"
import {
  StatsSettings,
  DataTable,
  SessionDetailsSettings,
  ScatterChartSettings,
  GraphSettings,
  TacticMapSettings,
  PerformanceProfileSettings,
} from "@/app/report/common/type"
import { Session } from "@/types/session"
import { SummaryType } from "@/types/report"
import { getFlatDepthReport } from "@/utils/report"
import { Summary } from "@/utils/definition"

// -- Daily Report ----------------------------------------------------------
abstract class Section {
  type: SECTION_TYPE = SECTION_TYPE.UNKNOWN
  isLoading = false
}

export class SectionSummary extends Section {
  summaries: Array<SummaryData>

  constructor(summaries: Array<SummaryData>) {
    super()
    this.type = SECTION_TYPE.SUMMARY
    this.summaries = summaries
  }
}

export class SectionGraph extends Section {
  charts: Array<ChartData>
  isLoading: boolean

  constructor(type: SECTION_TYPE, charts: Array<ChartData>) {
    super()
    this.type = type
    this.charts = charts
    this.isLoading = false
  }
}

export class SectionStats extends Section {
  title: string
  table: TableData

  constructor({
    type,
    title,
    statsTableData,
  }: {
    type: SECTION_TYPE
    title: string
    statsTableData: TableData
  }) {
    super()
    this.type = type
    this.title = title
    this.table = statsTableData
  }
}

export class SectionScatterChart extends Section {
  charts: Array<ChartScatterData>

  constructor(charts: Array<ChartScatterData>) {
    super()
    this.type = SECTION_TYPE.SCATTER_GRAPH
    this.charts = charts
  }
}

export class SectionTacticMap extends Section {
  title: string
  settings: TacticMapSettings

  constructor({
    type,
    title,
    settings,
  }: {
    type: SECTION_TYPE
    title: string
    settings: TacticMapSettings
  }) {
    super()
    this.type = type
    this.title = title
    this.settings = settings
  }
}

export class SectionPerformanceProfile extends Section {
  title: string
  settings: PerformanceProfileSettings

  constructor({
    type,
    title,
    settings,
  }: {
    type: SECTION_TYPE
    title: string
    settings: PerformanceProfileSettings
  }) {
    super()
    this.type = type
    this.title = title
    this.settings = settings
  }
}

// -- Chart By Player 관련 정의  ---------------------------------------------------------
export class ChartData {
  // emitEvent: EmitEvent;
  settings: GraphSettings
  sort: Sort
  axisY: Array<AxisY>
  data: Array<ChartRow>

  constructor(settings: GraphSettings, sort: Sort, axisY: Array<AxisY>, data: Array<ChartRow>) {
    // this.emitEvent = new EmitEvent();
    this.settings = settings
    this.sort = sort
    this.axisY = axisY
    this.data = data
  }
}

export class SummaryImpl implements Summary {
  private _data: Map<string, Map<string, number>>

  constructor() {
    this._data = new Map()
  }

  public get(key: string, summaryType: string): number | Map<string, number> | undefined {
    let trimmedKey = key

    // Custom, Rel suffix 제거 (metric key → 저장된 key로 변환)
    if (key.endsWith("Custom")) {
      trimmedKey = key.slice(0, -6)
    } else if (key.endsWith("Rel")) {
      trimmedKey = key.slice(0, -3)
    }

    const value = this._data.get(trimmedKey)

    if (value instanceof Map) {
      return value.get(summaryType)
    }

    return value
  }

  setReport(report: SummaryType | SummaryImpl) {
    Object.entries(report).forEach(([key, value]) => {
      if (typeof value === "number") {
        this.set(key, value, key)
      } else {
        const flatDepthReport = getFlatDepthReport(value as Record<string, unknown>)
        Object.entries(flatDepthReport).forEach(([type, value]) => {
          this.set(type, value as number, key)
        })
      }
    })
  }

  set(key: string, value: number, type: string) {
    if (this.has(key)) {
      return (this._data.get(key) as Map<string, number>).set(type, value)
    }

    const summary = new Map()
    summary.set(type, value)

    return this._data.set(key, summary)
  }

  has(key: string, type?: string): boolean {
    if (type !== undefined) {
      return this._data.has(key) && (this._data.get(key) as Map<string, number>).has(type)
    }

    return this._data.has(key)
  }

  delete(key: string) {
    return this._data.delete(key)
  }

  entries() {
    return Array.from(Object.freeze(this._data.entries()))
  }

  get mappedValue() {
    return Object.freeze(cloneDeep(this._data))
  }
}

// -- 정렬 관련 정의 -----------------------------------------------------------
export class Sort {
  key: string
  order: string

  constructor(key: string, order: string) {
    this.key = key
    this.order = order
  }
}

export class AxisY {
  name: string
  key: string
  unit: string | null
  decimalPoint: number
  color: string
  chartType: string
  flagShow: boolean
  manualScaleActive?: boolean
  manualScaleValue?: number | null

  constructor({
    name,
    key,
    unit,
    decimalPoint,
    color,
    chartType,
    flagShow,
    manualScaleActive,
    manualScaleValue,
  }: {
    name: string
    key: string
    unit: string | null
    decimalPoint: number
    color: string
    chartType: string
    flagShow: boolean
    manualScaleActive?: boolean
    manualScaleValue?: number | null
  }) {
    this.name = name
    this.key = key
    this.unit = unit
    this.decimalPoint = decimalPoint
    this.color = color
    this.chartType = chartType
    this.flagShow = flagShow
    this.manualScaleActive = manualScaleActive
    this.manualScaleValue = manualScaleValue
  }
}

export class ChartRow {
  name: string
  header: Map<string, string | number | null>
  data: Array<number | null>

  constructor(
    name: string | null = null,
    header: Map<string, string | number | null> | null = null,
    data: Array<number> | null = null,
  ) {
    if (name) {
      this.name = name
    } else {
      this.name = ""
    }

    if (header) {
      this.header = header
    } else {
      this.header = new Map<string, string | number | null>()
    }

    if (data) {
      this.data = data
    } else {
      this.data = []
    }
  }
}

// -- Stats Table 관련 정의  ---------------------------------------------------------
export class TableData {
  settings: StatsSettings
  sort: Sort
  tableHeader: TableHeaderColumn[]
  tableBody: DataTable
  sessionList: Array<Session>
  sessionSelectedRoot: Array<number>
  sessionSelected: Array<number>
  activitySessionType: ACTIVITY_SESSION_TYPE

  constructor({
    settings,
    sort,
    tableBody,
    tableHeader,
    sessionList,
    sessionSelected,
    sessionSelectedRoot,
    activitySessionType,
  }: {
    settings: StatsSettings
    sort: Sort
    tableHeader: TableHeaderColumn[]
    tableBody: DataTable
    sessionList: Array<Session>
    sessionSelectedRoot: Array<number>
    sessionSelected: Array<number>
    activitySessionType: ACTIVITY_SESSION_TYPE
  }) {
    this.settings = settings
    this.sort = sort
    this.tableHeader = tableHeader
    this.tableBody = tableBody
    this.sessionList = sessionList
    this.sessionSelectedRoot = sessionSelectedRoot
    this.sessionSelected = sessionSelected
    this.activitySessionType = activitySessionType
  }
}

// 테이블 헤더의 개별 컬럼 속성
export class TableHeaderColumn {
  key: string // metrics의 key
  title: string // metrics의 title
  unit: string | null // metrics의 unit
  decimalPoint: number | null // 소수점 자리수
  indexColumn: number // 정렬 시 사용할 컬럼의 인덱스
  average: string | number | null // 평균값
  total: string | number | null // 합계값
  isFixed: boolean | null // 고정 컬럼 여부

  constructor({
    key,
    title,
    unit,
    decimalPoint,
    indexColumn,
    isFixed = null,
  }: {
    key: string
    title: string
    unit: string | null
    decimalPoint: number | null
    indexColumn: number
    isFixed: boolean | null
  }) {
    this.key = key
    this.title = title
    this.unit = unit
    this.decimalPoint = decimalPoint
    this.indexColumn = indexColumn
    this.average = null
    this.total = null
    this.isFixed = isFixed
  }
}

// -- Chart Session Detail 관련 정의  ---------------------------------------------------------
export class IntervalChartData {
  name: string
  nickname: string | null
  data: Array<ChartRow>

  constructor(name: string, nickname: string | null, data: Array<ChartRow>) {
    this.name = name
    this.nickname = nickname
    this.data = data
  }
}

export class SessionDetailsData {
  // emitEventSession: EmitEvent;
  // emitEventInterval: EmitEvent;
  settings: SessionDetailsSettings
  interval: number
  axisY: Array<AxisY>
  sortSession: Sort
  dataSession: Array<ChartRow>
  dataInterval: Array<IntervalChartData>
  sessionList: Array<Session>
  sessionSelectedRoot: Array<number>
  sessionSelected: Array<number>
  activitySessionType: ACTIVITY_SESSION_TYPE

  constructor({
    settings,
    interval,
    axisY,
    sortSession,
    dataSession,
    dataInterval,
    sessionList,
    sessionSelectedRoot = [],
    sessionSelected = [],
    activitySessionType,
  }: {
    settings: SessionDetailsSettings
    interval: number
    axisY: Array<AxisY>
    sortSession: Sort
    dataSession: Array<ChartRow>
    dataInterval: Array<IntervalChartData>
    sessionList: Array<Session>
    sessionSelectedRoot?: Array<number>
    sessionSelected?: Array<number>
    activitySessionType: ACTIVITY_SESSION_TYPE
  }) {
    // this.emitEventSession = new EmitEvent()
    // this.emitEventInterval = new EmitEvent()
    this.settings = settings
    this.interval = interval
    this.axisY = axisY
    this.sortSession = sortSession
    this.dataSession = dataSession
    this.dataInterval = dataInterval
    this.sessionList = sessionList
    this.sessionSelectedRoot = sessionSelectedRoot
    this.sessionSelected = sessionSelected
    this.activitySessionType = activitySessionType
  }
}

// Tactic Table 헤더의 개별 컬럼 속성
export class TacticTableData {
  settings: TacticMapSettings
  sort: Sort
  tableHeader: TacticTableHeaderColumn[]
  tableBody: DataTable
  sessionList: Array<Session>
  sessionSelectedRoot: Array<number>
  sessionSelected: Array<number>
  activitySessionType: ACTIVITY_SESSION_TYPE

  constructor({
    settings,
    sort,
    tableBody,
    tableHeader,
    sessionList,
    sessionSelected,
    sessionSelectedRoot,
    activitySessionType,
  }: {
    settings: TacticMapSettings
    sort: Sort
    tableHeader: TacticTableHeaderColumn[]
    tableBody: DataTable
    sessionList: Array<Session>
    sessionSelectedRoot: Array<number>
    sessionSelected: Array<number>
    activitySessionType: ACTIVITY_SESSION_TYPE
  }) {
    this.settings = settings
    this.sort = sort
    this.tableHeader = tableHeader
    this.tableBody = tableBody
    this.sessionList = sessionList
    this.sessionSelectedRoot = sessionSelectedRoot
    this.sessionSelected = sessionSelected
    this.activitySessionType = activitySessionType
  }
}

// 테이블 헤더의 개별 컬럼 속성
export class TacticTableHeaderColumn {
  key: string // metrics의 key
  title: string // metrics의 title
  unit: string | null // metrics의 unit
  decimalPoint: number | null // 소수점 자리수
  indexColumn: number // 정렬 시 사용할 컬럼의 인덱스
  average: string | number | null // 평균값
  total: string | number | null // 합계값
  isFixed: boolean | null // 고정 컬럼 여부

  constructor({
    key,
    title,
    unit,
    decimalPoint,
    indexColumn,
    isFixed = null,
  }: {
    key: string
    title: string
    unit: string | null
    decimalPoint: number | null
    indexColumn: number
    isFixed: boolean | null
  }) {
    this.key = key
    this.title = title
    this.unit = unit
    this.decimalPoint = decimalPoint
    this.indexColumn = indexColumn
    this.average = null
    this.total = null
    this.isFixed = isFixed
  }
}

// -- Summary 관련 정의  ---------------------------------------------------------
export class SummaryData {
  title: string
  metric: string
  calcType: string
  data: number | null
  unit: string | null

  constructor({
    title,
    metric,
    calcType,
    data = null,
    unit = null,
  }: {
    title: string
    metric: string
    calcType: string
    data: number | null
    unit: string | null
  }) {
    this.title = title
    this.metric = metric
    this.data = data
    this.calcType = calcType
    this.unit = unit
  }
}

// -- Chart -----------------------------------------------------------
export class ChartMultiple {
  axisY: Array<AxisY>
  charts: Array<ChartMultipleItem>

  constructor(axisY: Array<AxisY>, charts: Array<ChartMultipleItem>) {
    this.axisY = axisY
    this.charts = charts
  }
}

export class ChartMultipleItem {
  name: string
  nickname: string | null
  rows: Array<ChartRow>
  maxValues: Array<number>

  constructor(
    name: string,
    nickname: string | null,
    chart: Array<ChartRow>,
    maxValues: Array<number> = [],
  ) {
    this.name = name
    this.nickname = nickname
    this.rows = chart
    this.maxValues = maxValues
  }
}

export class ChartScalable {
  axisY: Array<AxisY>
  rows: Array<ChartRow>

  constructor(axisY: Array<AxisY>, rows: Array<ChartRow>) {
    this.axisY = axisY
    this.rows = rows
  }
}

// -- Scatter Chart 관련 정의  ---------------------------------------------------------
export class ChartScatterData {
  // emitEvent: EmitEvent
  settings: ScatterChartSettings
  axisX: Axis
  axisY: Axis
  data: Array<ChartRow>

  constructor({
    settings,
    axisX,
    axisY,
    data,
  }: {
    settings: ScatterChartSettings
    axisX: Axis
    axisY: Axis
    data: Array<ChartRow>
  }) {
    // this.emitEvent = new EmitEvent()
    this.settings = settings
    this.axisX = axisX
    this.axisY = axisY
    this.data = data
  }
}

export class Axis {
  name: string
  decimalPoint: number
  unit: string

  constructor(name: string, decimalPoint: number, unit = "") {
    this.name = name
    this.decimalPoint = decimalPoint
    this.unit = unit
  }
}

export class Size {
  width: number
  height: number

  constructor(width: number, height: number) {
    this.width = width
    this.height = height
  }
}

export class Margin {
  top: number
  right: number
  bottom: number
  left: number

  constructor(top: number, right: number, bottom: number, left: number) {
    this.top = top
    this.right = right
    this.bottom = bottom
    this.left = left
  }
}

export class Rectangle {
  x: number
  y: number
  width: number
  height: number

  constructor({ x, y, width, height }: { x: number; y: number; width: number; height: number }) {
    this.x = x
    this.y = y
    this.width = width
    this.height = height
  }

  get top() {
    return this.y
  }

  get right() {
    return this.x + this.width
  }

  get bottom() {
    return this.y + this.height
  }

  get left() {
    return this.x
  }

  get cx() {
    return this.x + this.width / 2
  }

  get cy() {
    return this.y + this.height / 2
  }
}

export class SectionSessionDetails extends Section {
  sessionDetailsData: Array<SessionDetailsData>

  constructor(sessionDetailsData: Array<SessionDetailsData>) {
    super()
    this.type = SECTION_TYPE.SESSION_DETAILS
    this.sessionDetailsData = sessionDetailsData
  }
}

// -- ToolTip -----------------------------------------------------------
export class ToolTipContents {
  title: string
  value: string | null
  color: string | null

  constructor({
    title,
    value = null,
    color = null,
  }: {
    title: string
    value?: string | null
    color?: string | null
  }) {
    this.title = title
    this.value = value
    this.color = color
  }
}
